import { useEffect, useRef, useState, type ReactNode } from 'react';

interface TextRevealProps {
  children: string;
  as?: 'h1' | 'h2' | 'h3' | 'h4' | 'span' | 'p';
  className?: string;
  staggerMs?: number;
  durationMs?: number;
  distance?: number;
  threshold?: number;
  once?: boolean;
}

export default function TextReveal({
  children,
  as: Tag = 'span',
  className = '',
  staggerMs = 60,
  durationMs = 500,
  distance = 24,
  threshold = 0.3,
  once = true,
}: TextRevealProps) {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLElement>(null);
  const revealedRef = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !revealedRef.current) {
          revealedRef.current = true;
          setVisible(true);
          if (once) observer.disconnect();
        } else if (!once && !entry.isIntersecting) {
          revealedRef.current = false;
          setVisible(false);
        }
      },
      { threshold }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [once, threshold]);

  const words = children.split(' ');

  return (
    <Tag ref={ref as never} className={className}>
      {words.map((word, i) => (
        <span
          key={i}
          className="inline-block"
          style={{
            opacity: visible ? 1 : 0,
            transform: visible ? 'translateY(0)' : `translateY(${distance}px)`,
            transition: `opacity ${durationMs}ms cubic-bezier(0.25, 0.46, 0.45, 0.94), transform ${durationMs}ms cubic-bezier(0.25, 0.46, 0.45, 0.94)`,
            transitionDelay: `${i * staggerMs}ms`,
          }}
        >
          {word}
          {/* Preserve spaces with non-breaking approach — add space after each word except the last */}
          {i < words.length - 1 ? '\u00A0' : ''}
        </span>
      ))}
    </Tag>
  );
}